"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type AgedReference,
  type AgeReport,
  ageReport,
  type YearStatus,
} from "@/lib/tool-kit/reference-checkers/source-age";

/** English strings. A translated content file overrides these through `ui`. */
export const SOURCE_AGE_CHECKER_STRINGS = {
  listLabel: "Your reference list",
  listPlaceholder:
    "Paste a reference list in any style — APA, MLA, Chicago, Harvard, IEEE, Vancouver or AMA.",
  listHint:
    "One reference per line, or separate references with a blank line. Nothing is uploaded.",
  windowLabel: "Flag sources older than (years)",
  yearLabel: "Count back from the year",
  yearHint: "This year by default — change it if your paper is due next year.",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    "American Psychological Association. (2020). Publication manual of the American Psychological Association (7th ed.). https://doi.org/10.1037/0000165-000\nBandura, A. (1977). Self-efficacy: Toward a unifying theory of behavioral change. Psychological Review, 84(2), 191–215. https://doi.org/10.1037/0033-295X.84.2.191\nBender, E. M., Gebru, T., McMillan-Major, A., & Shmitchell, S. (2021). On the dangers of stochastic parrots: Can language models be too big? In Proceedings of the 2021 ACM Conference on Fairness, Accountability, and Transparency (pp. 610–623). https://doi.org/10.1145/3442188.3445922\nBraun, V., & Clarke, V. (2006). Using thematic analysis in psychology. Qualitative Research in Psychology, 3(2), 77–101. https://doi.org/10.1191/1478088706qp063oa\nBraun, V., & Clarke, V. (2021). One size fits all? What counts as quality practice in (reflexive) thematic analysis? Qualitative Research in Psychology, 18(3), 328–352. https://doi.org/10.1080/14780887.2020.1769238\nHattie, J., & Timperley, H. (2007). The power of feedback. Review of Educational Research, 77(1), 81–112. https://doi.org/10.3102/003465430298487\nKroenke, K., Spitzer, R. L., & Williams, J. B. W. (2001). The PHQ-9: Validity of a brief depression severity measure. Journal of General Internal Medicine, 16(9), 606–613. https://doi.org/10.1046/j.1525-1497.2001.016009606.x\nVaswani, A., Shazeer, N., Parmar, N., Uszkoreit, J., Jones, L., Gomez, A. N., Kaiser, Ł., & Polosukhin, I. (2017). Attention is all you need. Advances in Neural Information Processing Systems, 30.\nAuthor, A. (in press). Title of a forthcoming article. Journal Name.",
  emptyPrompt: "Paste a reference list to see how old its sources are.",
  invalidWindow: "Enter a whole number of years from 1 to 100.",
  invalidYear: "Enter a four-digit year, such as 2026.",
  results: "Results",
  cutoff:
    "Flagging sources published before {cutoff} — more than {window} years before {year}.",
  statWithin: "Published {cutoff} or later",
  statMedian: "Median age",
  statOlder: "Older than the window",
  statOldest: "Oldest source",
  statNewest: "Newest source",
  statNoYear: "Undated or no year found",
  years: "one:{count} year|other:{count} years",
  none: "—",
  chartHeading: "Sources by year",
  chartLabel: "References per publication year",
  barText: "one:{count} reference|other:{count} references",
  legendWithin: "Inside the window",
  legendOlder: "Older",
  inPress: "In press",
  tableHeading: "Every reference",
  colNumber: "#",
  colYear: "Year",
  colAge: "Age",
  colReference: "Reference",
  statuses: {
    dated: "",
    undated: "n.d.",
    "in-press": "In press",
    unknown: "No year found",
  } satisfies Record<YearStatus, string>,
  olderBadge: "Older",
  guidanceHeading: "When an older source is still the right one",
  guidance: [
    "A seminal or landmark study that a field still builds on — the theory, model or method you are using.",
    "A primary or historical source when the past is your subject.",
    "The original publication of a scale or instrument you used, and the validation study behind it.",
    "Laws, standards, classic texts and datasets cited for what they are, not for current findings.",
  ],
  guidanceNote:
    "Keep those, and make sure your claims about the current state of the field rest on recent work.",
} as const;

type Strings = typeof SOURCE_AGE_CHECKER_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const YEAR = { useGrouping: false } as const;

function readSettings(spanRaw: string, yearRaw: string) {
  const span = parseIntegerInput(spanRaw);
  const year = parseIntegerInput(yearRaw);
  return {
    span: span !== null && span >= 1 && span <= 100 ? span : null,
    year: year !== null && year >= 1500 && year <= 2500 ? year : null,
  };
}

function YearChart({
  report,
  strings,
}: {
  report: AgeReport;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const most = Math.max(
    1,
    report.inPress,
    ...report.byYear.map((row) => row.count)
  );
  const rows = [
    ...(report.inPress > 0
      ? [
          {
            key: "press",
            label: strings.inPress,
            count: report.inPress,
            old: false,
          },
        ]
      : []),
    ...report.byYear.map((row) => ({
      key: String(row.year),
      label: fmt(row.year, YEAR),
      count: row.count,
      old: row.old,
    })),
  ];
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <ul className="flex flex-wrap gap-4 text-muted-foreground text-xs">
        <li className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-3 w-3 rounded-sm bg-emerald-500/70"
          />
          {strings.legendWithin}
        </li>
        <li className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-3 w-3 rounded-sm bg-amber-500/70"
          />
          {strings.legendOlder}
        </li>
      </ul>
      <ul aria-label={strings.chartLabel} className="space-y-1.5">
        {rows.map((row) => (
          <li className="flex items-center gap-3 text-sm" key={row.key}>
            <span className="w-20 shrink-0 whitespace-nowrap text-right text-foreground tabular-nums">
              {row.label}
            </span>
            <span className="h-4 min-w-0 flex-1 rounded-sm bg-muted/50">
              <span
                aria-hidden="true"
                className={`block h-4 rounded-sm ${row.old ? "bg-amber-500/70" : "bg-emerald-500/70"}`}
                style={{ width: `${(row.count / most) * 100}%` }}
              />
            </span>
            <span className="w-28 shrink-0 text-muted-foreground text-xs">
              {tPlural(strings.barText, locale, row.count, {
                count: fmt(row.count),
              })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ageText(
  reference: AgedReference,
  strings: Strings,
  locale: string,
  fmt: Fmt
): string {
  if (reference.age === null) {
    return strings.none;
  }
  return tPlural(strings.years, locale, reference.age, {
    count: fmt(reference.age),
  });
}

function ReferenceTable({
  report,
  strings,
}: {
  report: AgeReport;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colNumber}
              </th>
              <th className={TH} scope="col">
                {strings.colYear}
              </th>
              <th className={TH} scope="col">
                {strings.colAge}
              </th>
              <th className={TH} scope="col">
                {strings.colReference}
              </th>
            </tr>
          </thead>
          <tbody>
            {report.references.map((reference) => (
              <tr key={reference.position}>
                <td className={`${TD} font-mono`}>{reference.position}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  {reference.year === null
                    ? strings.statuses[reference.status]
                    : fmt(reference.year, YEAR)}
                </td>
                <td className={`${TD} whitespace-nowrap`}>
                  {ageText(reference, strings, locale, fmt)}
                  {reference.old ? (
                    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800 text-xs dark:bg-amber-950 dark:text-amber-200">
                      {strings.olderBadge}
                    </span>
                  ) : null}
                </td>
                <td
                  className={`${TD} min-w-64 text-muted-foreground [overflow-wrap:anywhere]`}
                >
                  {reference.text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Results({
  report,
  span,
  year,
  strings,
}: {
  report: AgeReport;
  span: number;
  year: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const cutoff = fmt(report.cutoffYear, YEAR);
  const median =
    report.medianAge === null
      ? strings.none
      : tPlural(strings.years, locale, report.medianAge, {
          count: fmt(report.medianAge, { maximumFractionDigits: 1 }),
        });
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Notice>
        {t(strings.cutoff, {
          cutoff,
          window: fmt(span),
          year: fmt(year, YEAR),
        })}
      </Notice>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.statWithin, { cutoff })}
          value={
            report.shareWithin === null
              ? strings.none
              : fmt(report.shareWithin, {
                  style: "percent",
                  maximumFractionDigits: 0,
                })
          }
        />
        <StatCard label={strings.statMedian} value={median} />
        <StatCard label={strings.statOlder} value={fmt(report.older)} />
        <StatCard
          label={strings.statOldest}
          value={
            report.oldest === null ? strings.none : fmt(report.oldest, YEAR)
          }
        />
        <StatCard
          label={strings.statNewest}
          value={
            report.newest === null ? strings.none : fmt(report.newest, YEAR)
          }
        />
        <StatCard
          label={strings.statNoYear}
          value={fmt(report.undated + report.unknown)}
        />
      </StatGrid>
      {report.byYear.length > 0 || report.inPress > 0 ? (
        <YearChart report={report} strings={strings} />
      ) : null}
      <ReferenceTable report={report} strings={strings} />
    </section>
  );
}

export default function SourceAgeCheckerWidget() {
  const strings = useToolUi(SOURCE_AGE_CHECKER_STRINGS);
  const [list, setList] = useState("");
  const [spanRaw, setSpanRaw] = useState("5");
  // The current year is read after mount, so the server and first client render agree.
  const [yearRaw, setYearRaw] = useState("");
  useEffect(() => {
    setYearRaw((current) =>
      current === "" ? String(new Date().getFullYear()) : current
    );
  }, []);
  const deferred = useDeferredValue(list);
  const settings = readSettings(spanRaw, yearRaw);
  const report = useMemo(
    () =>
      deferred.trim() && settings.span !== null && settings.year !== null
        ? ageReport(deferred, settings.year, settings.span)
        : null,
    [deferred, settings.span, settings.year]
  );

  return (
    <>
      <TextAreaField
        hint={strings.listHint}
        id="sac-list"
        label={strings.listLabel}
        onChange={setList}
        placeholder={strings.listPlaceholder}
        prose
        rows={10}
        value={list}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="sac-window"
          label={strings.windowLabel}
          numeric
          onChange={setSpanRaw}
          value={spanRaw}
        />
        <TextField
          hint={strings.yearHint}
          id="sac-year"
          label={strings.yearLabel}
          numeric
          onChange={setYearRaw}
          value={yearRaw}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setList(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={list.length === 0}
          onClick={() => setList("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {settings.span === null ? (
        <ErrorNotice>{strings.invalidWindow}</ErrorNotice>
      ) : null}
      {yearRaw !== "" && settings.year === null ? (
        <ErrorNotice>{strings.invalidYear}</ErrorNotice>
      ) : null}
      {report && settings.span !== null && settings.year !== null ? (
        <Results
          report={report}
          span={settings.span}
          strings={strings}
          year={settings.year}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H3}>{strings.guidanceHeading}</h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          {strings.guidance.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <p className={HINT}>{strings.guidanceNote}</p>
      </section>
    </>
  );
}
